/**
 * This is the main entry point for the UI. You should not need to make any
 * changes here unless you want to update the theme.
 *
 * @see https://github.com/allenai/varnish-mui
 */

import { App } from '@/App';
import { Auth0Provider } from '@/auth/Auth0Client';
import { getLayoverLogger } from '@/logging/LayoverLogger';
import { listenForTrackEvents } from '@/analytics/clickTracking';
import { registerDatePlugins } from '@/utils/dateUtils';
import { store } from '@/store/store';
import { Toasts } from '@/components/system/Toasts';
import { trackPerformanceMetrics } from '@/analytics/performanceMetrics';

import { createRoot } from 'react-dom/client';
import { getRouterOverriddenTheme, VarnishApp } from '@allenai/varnish2';
import { getTheme } from '@allenai/varnish2/theme';
import { Link } from 'react-router-dom';
import { Provider } from 'react-redux';
import { ThemeProvider } from 'styled-components';

// set up dayjs with plugins
registerDatePlugins();

const VarnishedApp = () => {
  const theme = getTheme(getRouterOverriddenTheme(Link));

  return (
    <ThemeProvider theme={theme}>
      <VarnishApp theme={theme}>
        <Provider store={store}>
          <Auth0Provider>
            <App />
            <Toasts />
          </Auth0Provider>
        </Provider>
      </VarnishApp>
    </ThemeProvider>
  );
};

const container = document.getElementById('root');
if (!container) {
  throw new Error("No element with an id of 'root' was found.");
}

listenForTrackEvents(document.body);
listenForUncaughtErrors();
trackPerformanceMetrics();

const root = createRoot(container);
root.render(<VarnishedApp />);

function listenForUncaughtErrors() {
  window.addEventListener('error', (event) => {
    getLayoverLogger().logErrorEvent(event);
  });
}
